/* =========================================================================
   QUICK PRO — Quick Ship landing shelf behavior (phase 2, rebuild)
   -------------------------------------------------------------------------
   <qp-qs-shelf> — client-side behavior over the server-rendered rail
   (sections/qp-qs-shelf.liquid):

   1. FACETS — SIZE / STOCK COLOR / CATEGORY / GROUPSET. Desktop: inline bar with
      popover panels + mono result count (1 BUILD / N BUILDS, Discovery
      A1 grammar) left of CLEAR ALL. Mobile (<768): FULL-SCREEN
      SLIDE-OVER (signed ruling — the stacked-accordion simplification
      is retired): FILTER trigger opens the same facet DOM as a fixed
      panel, groups always expanded, SHOW N BUILDS foot. Facets DROP
      non-matching rows and DIM non-matching size/color cells.
      Options are harvested from the rendered rows (stocked values only,
      in appearance order); preview_state force_* flows through for free
      because it already marked every chip stocked server-side. An
      option-less GROUPSET facet is REMOVED from the DOM (a hidden flex
      child still costs a gap slot — designer P2).

   2. GROUPSET popover grouping — values group under mono subheads
      SHIMANO / SRAM (brand inferred from the value: SRAM families
      FORCE/RED/RIVAL/APEX/EAGLE or a value starting with SRAM; Shimano
      families DURA-ACE/ULTEGRA/105/TIAGRA/GRX/XT/XTR/SLX/DEORE).
      Unmatched values form an OTHER bucket, rendered last, never
      headed. A shelf where every present value maps to a single brand
      renders flat (no lone subhead) — grouping only appears once both
      brands are present. Subheads are labels, not filters. CATEGORY
      renders in the fixed order ROAD / GRAVEL / TRI/TT / MTB
      regardless of row appearance order.

   3. QS INTERLEAF band placement — bands are section blocks rendered
      into the foot container; this file owns placement. Rules
      (designer-final): never above row 1; band position is min 2 (min
      two rows before the first band); 1 band per 3 VISIBLE rows; hard
      max 2 in-rail; never two bands at the same row index. Bands pin to
      row INDEX, are never counted in the result count, and never
      disappear under filters — when filtering leaves fewer rows than a
      band's index (or the rail drops below 3 rows: feature mode), the
      band collapses to the foot in source order. Placement re-runs on
      every filter apply. No JS at all = every band at the foot, which
      is the designed short-shelf state anyway.
   ========================================================================= */
(function () {
  'use strict';

  var FACETS = ['size', 'color', 'category', 'group'];

  /* CATEGORY facet — fixed display order, independent of row appearance
     order (spec: ROAD, GRAVEL, TRI/TT, MTB). */
  var CATEGORY_ORDER = ['ROAD', 'GRAVEL', 'TRI/TT', 'MTB'];

  /* GROUPSET popover brand inference — mirrors the Liquid-side comment
     in sections/qp-qs-shelf.liquid. Values not matching either family
     list fall to OTHER (rendered last, never headed). */
  var SRAM_FAMILIES = ['FORCE', 'RED', 'RIVAL', 'APEX', 'EAGLE'];
  var SHIMANO_FAMILIES = ['DURA-ACE', 'ULTEGRA', '105', 'TIAGRA', 'GRX', 'XT', 'XTR', 'SLX', 'DEORE'];
  function classifyGroupsetBrand(value) {
    var v = (value || '').toUpperCase();
    if (v.indexOf('SRAM') === 0) return 'SRAM';
    var i;
    for (i = 0; i < SRAM_FAMILIES.length; i++) {
      if (new RegExp('\\b' + SRAM_FAMILIES[i] + '\\b').test(v)) return 'SRAM';
    }
    for (i = 0; i < SHIMANO_FAMILIES.length; i++) {
      if (new RegExp('\\b' + SHIMANO_FAMILIES[i] + '\\b').test(v)) return 'SHIMANO';
    }
    return 'OTHER';
  }

  function splitVals(attr) {
    var out = [], seen = {};
    (attr || '').split('|').forEach(function (v) {
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    });
    return out;
  }
  function has(arr, v) { return arr.indexOf(v) !== -1; }
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  if (customElements.get('qp-qs-shelf')) return;
  customElements.define('qp-qs-shelf', class extends HTMLElement {
    connectedCallback() {
      if (this._initDone) return;
      this._initDone = true;
      onReady(this._setup.bind(this));
    }

    _setup() {
      var self = this;
      this.filterbar = this.querySelector('[data-qs-filterbar]');
      this.shell = this.querySelector('[data-qs-shell]');
      this.activeRow = this.querySelector('[data-qs-active]');
      this.activeChips = this.querySelector('[data-qs-active-chips]');
      this.emptyEl = this.querySelector('[data-qs-empty]');
      this.countEl = this.querySelector('[data-qs-count]');
      this.footEl = this.querySelector('[data-qs-bands-foot]');
      this.triggerCount = this.querySelector('[data-qs-filter-count]');
      this.showBtn = this.querySelector('[data-qs-filter-show]');
      this.showLabel = this.showBtn ? this.showBtn.textContent.trim() : 'SHOW';

      /* rows: stocked values pre-deduped from pipe-joined data attrs */
      this.rows = [];
      this.querySelectorAll('[data-qs-row]').forEach(function (el) {
        self.rows.push({
          el: el,
          sizes: splitVals(el.getAttribute('data-sizes')),
          colors: splitVals(el.getAttribute('data-colors')),
          group: el.getAttribute('data-groupset') || '',
          category: el.getAttribute('data-category') || ''
        });
      });

      /* QS INTERLEAF bands, source order (server renders them at the foot) */
      this.bands = [];
      this.querySelectorAll('[data-qs-band]').forEach(function (el) {
        self.bands.push({
          el: el,
          pos: Math.max(2, parseInt(el.getAttribute('data-position'), 10) || 2),
          placement: el.getAttribute('data-placement') || 'foot'
        });
      });

      this.state = { size: [], color: [], category: [], group: [] };
      this.facetEls = {};
      if (!this.rows.length) return;

      this._buildFacets();
      this._bindFilterSheet();
      this._apply();
    }

    _buildFacets() {
      var self = this;
      if (!this.filterbar || !this.shell) return;

      /* facet options in appearance order across rows */
      this.options = { size: [], color: [], category: [], group: [] };
      this.rows.forEach(function (r) {
        r.sizes.forEach(function (v) { if (!has(self.options.size, v)) self.options.size.push(v); });
        r.colors.forEach(function (v) { if (!has(self.options.color, v)) self.options.color.push(v); });
        /* r.group is '' for frames (no groupset) — never an option */
        if (r.group && !has(self.options.group, r.group)) self.options.group.push(r.group);
        /* r.category is '' when unresolved — never an option */
        if (r.category && !has(self.options.category, r.category)) self.options.category.push(r.category);
      });
      /* CATEGORY renders in the fixed spec order, not appearance order */
      this.options.category = CATEGORY_ORDER.filter(function (c) { return has(self.options.category, c); });

      var anyOptions = false;
      FACETS.forEach(function (key) {
        var wrap = self.shell.querySelector('[data-qs-facet="' + key + '"]');
        if (!wrap) return;
        var opts = self.options[key];
        if (!opts.length || ((key === 'group' || key === 'category') && opts.length < 2)) {
          /* REMOVE, don't hide — a hidden flex child still occupies a
             gap slot in the bar (designer P2). GROUPSET and CATEGORY
             both need >=2 distinct values to be worth a filter. */
          wrap.parentNode.removeChild(wrap);
          return;
        }
        anyOptions = true;
        self.facetEls[key] = {
          wrap: wrap,
          btn: wrap.querySelector('[data-qs-facet-btn]'),
          count: wrap.querySelector('[data-qs-facet-count]'),
          panel: wrap.querySelector('[data-qs-facet-panel]'),
          label: wrap.getAttribute('data-facet-label') || key.toUpperCase()
        };
        if (key === 'group') {
          self._buildGroupsetOptions(self.facetEls[key].panel, opts);
        } else {
          opts.forEach(function (value) {
            self.facetEls[key].panel.appendChild(self._makeOptBtn(value));
          });
        }
      });
      if (!anyOptions) return; /* bar stays hidden — nothing to filter */
      this.filterbar.hidden = false;

      this.filterbar.addEventListener('click', function (e) {
        if (e.target.closest('[data-qs-filter-open]')) { self._openSheet(true); return; }
        if (e.target.closest('[data-qs-filter-close]')) { self._openSheet(false); return; }
        if (e.target.closest('[data-qs-filter-show]')) { self._openSheet(false); return; }
        if (e.target.closest('[data-qs-clear]')) { self._clearAll(); return; }
        var btn = e.target.closest('[data-qs-facet-btn]');
        if (btn) { self._togglePanel(btn.closest('[data-qs-facet]').getAttribute('data-qs-facet')); return; }
        var opt = e.target.closest('[data-qs-opt]');
        if (opt) {
          var key = opt.closest('[data-qs-facet]').getAttribute('data-qs-facet');
          self._toggleValue(key, opt.getAttribute('data-qs-opt'));
        }
      });
      this.activeRow.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-qs-active-chip]');
        if (chip) self._toggleValue(chip.getAttribute('data-facet'), chip.getAttribute('data-value'));
      });
      /* the no-match line's clear button sits OUTSIDE the filter bar, so
         the bar's click delegate never sees it — bind it directly */
      var nomatchClear = this.emptyEl ? this.emptyEl.querySelector('[data-qs-clear]') : null;
      if (nomatchClear) nomatchClear.addEventListener('click', function () { self._clearAll(); });
      /* click-away + Escape close the open popover panel */
      document.addEventListener('click', function (e) {
        if (!self.contains(e.target)) self._togglePanel(null);
      });
      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (self._sheetOpen) self._openSheet(false);
        else self._togglePanel(null);
      });
    }

    _makeOptBtn(value) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'qp-qs-shelf__opt';
      b.textContent = value;
      b.setAttribute('data-qs-opt', value);
      b.setAttribute('aria-pressed', 'false');
      return b;
    }

    /* GROUPSET popover — group under SHIMANO / SRAM mono subheads once
       both brands are present on the shelf; a single-brand shelf (with
       or without OTHER values mixed in) renders flat, exactly as before.
       Subheads are plain labels — they carry no data-qs-opt attribute so
       the click delegate and _apply()'s aria-pressed sweep both ignore
       them. Same panel DOM serves the mobile slide-over for free. */
    _buildGroupsetOptions(panel, opts) {
      var self = this;
      var buckets = { SHIMANO: [], SRAM: [], OTHER: [] };
      opts.forEach(function (v) { buckets[classifyGroupsetBrand(v)].push(v); });
      var brandCount = (buckets.SHIMANO.length ? 1 : 0) + (buckets.SRAM.length ? 1 : 0);
      if (brandCount < 2) {
        opts.forEach(function (v) { panel.appendChild(self._makeOptBtn(v)); });
        return;
      }
      ['SHIMANO', 'SRAM'].forEach(function (brand) {
        if (!buckets[brand].length) return;
        var head = document.createElement('span');
        head.className = 'qp-qs-shelf__opt-subhead';
        head.textContent = brand;
        panel.appendChild(head);
        buckets[brand].forEach(function (v) { panel.appendChild(self._makeOptBtn(v)); });
      });
      buckets.OTHER.forEach(function (v) { panel.appendChild(self._makeOptBtn(v)); });
    }

    /* ---- mobile full-screen slide-over ---------------------------------- */
    _bindFilterSheet() {
      this._sheetOpen = false;
    }
    _openSheet(open) {
      var self = this;
      if (!this.shell) return;
      this._sheetOpen = !!open;
      this.shell.classList.toggle('is-open', this._sheetOpen);
      document.body.classList.toggle('qp-qs-filter-open', this._sheetOpen);
      if (this._sheetOpen) {
        /* groups are always expanded in the slide-over. The [hidden]
           attr must come OFF: base.css ships [hidden]{display:none
           !important}, which no selector can outrank. Closing restores
           the desktop popover contract (all panels hidden). */
        FACETS.forEach(function (k) {
          var f = self.facetEls[k];
          if (f) f.panel.hidden = false;
        });
      } else {
        this._togglePanel(null);
      }
    }

    _togglePanel(key) {
      var self = this;
      FACETS.forEach(function (k) {
        var f = self.facetEls[k];
        if (!f) return;
        var open = k === key && f.panel.hidden;
        f.panel.hidden = !open;
        f.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    _toggleValue(key, value) {
      var sel = this.state[key];
      if (!sel) return;
      var i = sel.indexOf(value);
      if (i === -1) sel.push(value); else sel.splice(i, 1);
      this._apply();
    }

    _clearAll() {
      this.state = { size: [], color: [], category: [], group: [] };
      this._togglePanel(null);
      this._apply();
    }

    /* OR within a facet, AND across facets — over STOCKED values */
    _matches(row) {
      var s = this.state;
      if (s.size.length && !row.sizes.some(function (v) { return has(s.size, v); })) return false;
      if (s.color.length && !row.colors.some(function (v) { return has(s.color, v); })) return false;
      /* Unresolved category (row.category === '') matches no active
         category filter — the row shows only while the facet is
         inactive, same contract as the groupset blank-drop below. */
      if (s.category.length && !has(s.category, row.category)) return false;
      /* INTENTIONAL: frames carry NO groupset (row.group === '') so any
         active groupset filter drops them — a frame genuinely does not
         match "SRAM FORCE AXS". Blank never becomes a facet option
         either (see the harvest in _buildFacets). */
      if (s.group.length && !has(s.group, row.group)) return false;
      return true;
    }

    /* QS INTERLEAF placement — runs on every apply. Bands pin to VISIBLE
       row index; ineligible bands collapse to the foot in source order. */
    _placeBands(visible) {
      if (!this.bands.length || !this.footEl) return;
      var self = this;
      var V = visible.length;
      var maxInRail = V >= 3 ? Math.min(2, Math.floor(V / 3)) : 0;
      var used = [];
      this.bands.forEach(function (b) {
        var inRail = b.placement === 'in_rail' &&
          used.length < maxInRail &&
          b.pos >= 2 && b.pos <= V &&
          !has(used, b.pos);
        if (inRail) {
          visible[b.pos - 1].el.insertAdjacentElement('afterend', b.el);
          b.el.classList.add('is-in-rail');
          used.push(b.pos);
        } else {
          b.el.classList.remove('is-in-rail');
          self.footEl.appendChild(b.el); /* iteration order = source order */
        }
      });
    }

    _apply() {
      var self = this, s = this.state, visible = [];
      this.rows.forEach(function (row) {
        var ok = self._matches(row);
        row.el.classList.toggle('is-dropped', !ok);
        if (ok) visible.push(row);
        /* dim non-matching cells inside kept rows (design ruling) */
        row.el.querySelectorAll('[data-qs-cell]').forEach(function (cell) {
          cell.classList.toggle('is-dimmed', s.size.length > 0 && !has(s.size, cell.getAttribute('data-size')));
        });
        row.el.querySelectorAll('[data-qs-color]').forEach(function (grp) {
          var v = grp.getAttribute('data-qs-color');
          grp.classList.toggle('is-dimmed', s.color.length > 0 && !!v && !has(s.color, v));
        });
      });
      var shown = visible.length;
      if (this.emptyEl) this.emptyEl.hidden = shown > 0;

      /* mono result count — rows only, bands are NEVER counted */
      if (this.countEl) {
        this.countEl.textContent = shown + ' ' +
          (shown === 1 ? this.countEl.getAttribute('data-one') || 'BUILD'
                       : this.countEl.getAttribute('data-many') || 'BUILDS');
      }
      if (this.showBtn) {
        this.showBtn.textContent = this.showLabel + ' ' + shown + ' ' +
          (shown === 1 ? 'BUILD' : 'BUILDS');
      }

      this._placeBands(visible);

      var anyActive = s.size.length + s.color.length + s.category.length + s.group.length > 0;

      /* facet buttons: pressed marks + counts */
      FACETS.forEach(function (key) {
        var f = self.facetEls[key];
        if (!f) return;
        f.panel.querySelectorAll('[data-qs-opt]').forEach(function (b) {
          var on = has(s[key], b.getAttribute('data-qs-opt'));
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        f.count.textContent = s[key].length ? '(' + s[key].length + ')' : '';
      });

      /* CLEAR ALL (bar + slide-over foot) + mobile trigger count */
      this.querySelectorAll('[data-qs-clear]').forEach(function (b) { b.hidden = !anyActive; });
      if (this.triggerCount) {
        var n = s.size.length + s.color.length + s.category.length + s.group.length;
        this.triggerCount.textContent = n ? ' (' + n + ')' : '';
      }

      /* active-chip row */
      if (!this.activeRow) return;
      this.activeRow.hidden = !anyActive;
      this.activeChips.textContent = '';
      if (anyActive) {
        FACETS.forEach(function (key) {
          var f = self.facetEls[key];
          s[key].forEach(function (value) {
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'qp-qs-shelf__active-chip';
            chip.setAttribute('data-qs-active-chip', '');
            chip.setAttribute('data-facet', key);
            chip.setAttribute('data-value', value);
            chip.textContent = (f ? f.label : key.toUpperCase()) + ' · ' + value + ' ×';
            self.activeChips.appendChild(chip);
          });
        });
      }
    }
  });
})();
